/**
 * trackingAdapter — turns the public /v2/tracking/{term} response into the
 * `result` object <TrackPageView /> expects. Pure functions, no fetching.
 * Works for ALL existing shipments (read-time mapping, no DB migration).
 * Only the already-sanitised display_* fields are ever shown.
 */

const norm = (t) =>
  String(t || "")
    .toUpperCase()
    .replace(/^(3PL|FM|LM)_/, "")
    .replace(/[^A-Z0-9]+/g, "_");

/** event_type -> { stage 1-8 | null, state } */
export function classifyEvent(eventType) {
  const k = norm(eventType);
  if (!k) return { stage: null, state: "normal" };
  if (/RTO|RETURN/.test(k)) return { stage: null, state: "returned" };
  if (/LOST|DAMAG|CANCEL|HOLD|EXCEPTION|UNDELIVER/.test(k)) return { stage: null, state: "exception" };
  if (/NDR|ATTEMPT/.test(k)) return { stage: 7, state: "exception" };
  if (/OUT_FOR_DELIVERY|^OFD$/.test(k)) return { stage: 7, state: "normal" };
  if (/DELIVERED/.test(k) && !/UN/.test(k)) return { stage: 8, state: "normal" };
  if (/REACHED|ARRIVED|AT_DEST|DESTINATION|HUB|UNLOAD|RECEIVED/.test(k)) return { stage: 6, state: "normal" };
  if (/IN_TRANSIT|TRANSIT|ENROUTE/.test(k)) return { stage: 5, state: "normal" };
  if (/DISPATCH|LINEHAUL|LINE_HAUL|DEPART|FLIGHT|TRAIN|VESSEL|SHIPPED/.test(k)) return { stage: 4, state: "normal" };
  if (/LOADED|SORT|BAG|MANIFEST_OUT|IN_SCAN|INSCAN/.test(k)) return { stage: 3, state: "normal" };
  if (/PICKUP_(PENDING|SCHEDULED|REQUESTED|FAILED)|OUT_FOR_PICKUP/.test(k)) return { stage: 1, state: "normal" };
  if (/PICKED|PICKUP/.test(k)) return { stage: 2, state: "normal" };
  if (/BOOK|MANIFEST|CREATED|PENDING|PLACED|CONFIRMED/.test(k)) return { stage: 1, state: "normal" };
  return { stage: null, state: "normal" };
}

export function detectMode(data) {
  const raw = String(data?.transport_mode || data?.mode || data?.shipment_mode || "").toLowerCase();
  if (/rail|train/.test(raw)) return "rail";
  if (/air|flight/.test(raw)) return "air";
  if (/sea|ship|ocean|vessel/.test(raw)) return "sea";
  return "road"; // surface / FTL / PTL / anything unknown
}

const prettify = (t) =>
  String(t || "")
    .replace(/^3PL_/, "Courier ")
    .replace(/_/g, " ")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());

const fmtDate = (s) => {
  if (!s) return "";
  try {
    return new Date(s).toLocaleString("en-IN", {
      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return "";
  }
};

export function toResult(data, term) {
  const asc = [...(data?.events || [])].sort(
    (a, b) => new Date(a.event_date || 0) - new Date(b.event_date || 0)
  );

  let reached = 1;
  let state = "normal";
  for (const ev of asc) {
    const c = classifyEvent(ev.event_type);
    if (c.stage) reached = Math.max(reached, c.stage);
    state = c.state === "normal" && c.stage ? "normal" : c.state; // latest event decides
  }
  const topStatus = classifyEvent(data?.current_status);
  if (!asc.length) {
    if (topStatus.stage) reached = topStatus.stage;
    state = topStatus.state;
  }
  if (reached === 8) state = "normal";

  const desc = [...asc].reverse();
  const latest = desc[0];
  const events = desc.map((ev) => {
    const c = classifyEvent(ev.event_type);
    return {
      stage: c.stage || reached,
      label: ev.display_message || prettify(ev.event_type),
      city: ev.display_location || "",
      time: fmtDate(ev.event_date),
    };
  });

  return {
    number: term,
    status: prettify(data?.current_status || latest?.event_type) || "In progress",
    stage: reached,
    mode: detectMode(data),
    state,
    origin: data?.origin_city || "",
    destination: data?.destination_city || "",
    eta: data?.expected_delivery || data?.edd || "",
    updatedAt: fmtDate(latest?.event_date),
    events,
  };
}
